import { cn } from "@acme/ui";
import { useId } from "react";
import { AppIcon } from "~/components/app-icon";
import { getWorkbenchTasks } from "~/lib/constants/workbench";
import * as m from "~/paraglide/messages.js";
import { Artifact } from "./artifacts";
import { Inspector } from "./inspector";
import { TaskTablist } from "./task-tablist";
import { getWorkbenchStatus, useWorkbench } from "./use-workbench";

/**
 * The page's thesis: pick a recurring task, see what the system prepared, see
 * what it used, and decide. It is an app-chrome slab dropped into an editorial
 * page — the one place the site shows the product rather than describing it.
 *
 * The five panels share a single `tabpanel` whose `aria-labelledby` rotates to
 * the active tab, matching the approved design. That means only the selected
 * task is in the DOM; the trade is deliberate and noted in the surface brief.
 */
export function Workbench() {
  const titleId = useId();
  const panelId = useId();
  const tasks = getWorkbenchTasks();
  const { decision, reset, selectTask, selected, setDecision } = useWorkbench();

  const task = tasks[selected];
  const status = getWorkbenchStatus(task, decision);

  return (
    <section aria-labelledby={titleId} className="work wrap" id="work">
      <div className="work-caption">
        <h2 id={titleId}>{m.home_work_title()}</h2>
        <span>{m.home_work_caption()}</span>
      </div>

      <div className="workbench">
        <div className="bench-top">
          <span className="bench-name">
            <img alt="" height={22} src="/images/icon.svg" width={22} />
            {m.home_bench_name()}
          </span>
          <div className="bench-controls">
            <span>{m.home_bench_interactive()}</span>
            <button
              aria-label={m.home_bench_reset_label()}
              className="reset"
              onClick={reset}
              type="button"
            >
              <span aria-hidden="true">↻</span> {m.home_bench_reset()}
            </button>
          </div>
        </div>

        <div className="bench-body">
          <TaskTablist
            onSelect={selectTask}
            panelId={panelId}
            selected={selected}
            tasks={tasks}
          />
          <div
            aria-labelledby={`tab-${selected}`}
            className="task-panel"
            data-settling="true"
            id={panelId}
            // Remounting on task change is what replays the settle animation;
            // it also guarantees the artifact's own state starts clean.
            key={selected}
            role="tabpanel"
            // biome-ignore lint/a11y/noNoninteractiveTabindex: a tabpanel with no focusable content must itself be focusable (WAI-ARIA tabs pattern)
            tabIndex={0}
          >
            <div className="task-intro">
              <p className="task-channel">
                <AppIcon name={task.icon} />
                {task.channel} <span>{task.context}</span>
              </p>
              <h3>
                {task.title[0]} <br />
                {task.title[1]}
              </h3>
            </div>

            <Artifact artifact={task.artifact} />

            <div className="task-result">
              <span aria-hidden="true">↳</span>
              <p>{task.result}</p>
            </div>
          </div>

          <Inspector
            decision={decision}
            description={status.description}
            label={status.label}
            needsPerson={task.needsPerson ?? false}
            onApprove={() => setDecision("approved")}
            onHold={() => setDecision("held")}
            sources={task.sources}
          />
        </div>

        <div className="bench-bottom">
          {/* Mounted unconditionally so the live region exists before its text
              changes — a region added at the same moment announces nothing. */}
          <span
            className={cn("run-state", status.isComplete && "is-complete")}
            role="status"
          >
            {status.state}
          </span>
          <span>{m.home_bench_disclaimer()}</span>
        </div>
      </div>
    </section>
  );
}
